import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export default function ProductTour({ steps, tourKey, onComplete }) {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // No auto-launch — tour is ONLY triggered manually via startTour()

  const step = steps[idx];

  // Measure target element position
  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    if (!active) return;
    measure();
    const iv = setInterval(measure, 300);
    return () => clearInterval(iv);
  }, [active, idx, measure]);

  // Keyboard
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const finish = () => {
    setActive(false);
    if (onComplete) onComplete();
  };
  const next = () => { if (idx < steps.length - 1) setIdx(idx + 1); else finish(); };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };

  // Public: allow external trigger
  useEffect(() => {
    const handler = () => { setIdx(0); setActive(true); };
    window.addEventListener(`start-tour-${tourKey}`, handler);
    return () => window.removeEventListener(`start-tour-${tourKey}`, handler);
  }, [tourKey]);

  if (!active || !step) return null;

  const isFallback = step.fallback || !rect;
  const pad = 8;

  // Tooltip position
  let tooltipStyle = {};
  if (!isFallback && rect) {
    const pos = step.position || 'bottom';
    if (pos === 'bottom') tooltipStyle = { top: rect.top + rect.height + pad + 12, left: Math.max(16, rect.left), maxWidth: 360 };
    else if (pos === 'top') tooltipStyle = { bottom: window.innerHeight - rect.top + pad + 12, left: Math.max(16, rect.left), maxWidth: 360 };
    else if (pos === 'right') tooltipStyle = { top: rect.top, left: rect.left + rect.width + pad + 12, maxWidth: 340 };
    else if (pos === 'left') tooltipStyle = { top: rect.top, right: window.innerWidth - rect.left + pad + 12, maxWidth: 340 };
  }

  return createPortal(
    <div className="ug-modal fixed inset-0 z-[99999]">
      {/* Overlay with cutout */}
      {!isFallback && rect ? (
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect x={rect.left - pad} y={rect.top - pad} width={rect.width + pad * 2} height={rect.height + pad * 2} rx="8" fill="black" />
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#tour-mask)" style={{ pointerEvents: 'all' }} onClick={next} />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-black/70" onClick={next} />
      )}

      {/* Spotlight border */}
      {!isFallback && rect && (
        <div className="absolute rounded-lg border-2 border-gold-leaf/60 pointer-events-none" style={{
          top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: '0 0 0 4px rgba(212,201,160,0.15), 0 0 30px rgba(212,201,160,0.1)',
        }} />
      )}

      {/* Tooltip card */}
      <div className={`absolute bg-midnight border border-white/10 rounded-lg shadow-2xl p-5 ${isFallback ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] max-w-[90vw]' : ''}`}
        style={isFallback ? {} : tooltipStyle}>
        {/* Close */}
        <button onClick={finish} className="absolute top-3 right-3 text-slate-500 hover:text-silk-cream transition-colors">
          <X className="w-4 h-4" />
        </button>

        <h3 data-role="section-title" className="text-silk-cream font-heading text-base mb-2 pr-6">{step.title}</h3>
        <p className="text-[12px] text-slate-400 leading-relaxed mb-4">{step.desc}</p>

        {/* Nav */}
        <div className="flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-gold-leaf' : i < idx ? 'bg-gold-leaf/40' : 'bg-white/10'}`} />
            ))}
          </div>
          {/* Buttons */}
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={prev} className="flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-silk-cream transition-colors">
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            )}
            <button onClick={next}
              className="flex items-center gap-1 px-4 py-1.5 rounded-md bg-gold-leaf text-obsidian font-mono text-[10px] uppercase tracking-wider hover:bg-gold-leaf/90 transition-colors">
              {idx === steps.length - 1 ? 'Done' : 'Next'} {idx < steps.length - 1 && <ChevronRight className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Step counter */}
        <p className="text-[9px] text-slate-600 text-center mt-3">{idx + 1} of {steps.length} — press Esc to skip</p>
      </div>
    </div>,
    document.body
  );
}

// Helper to trigger tour from anywhere
export function startTour(role) {
  window.dispatchEvent(new Event(`start-tour-${role}`));
}
